import React, { useState, useEffect, useRef } from 'react';
import { X, User, Mail, Activity, Loader2, Globe, Clock, RefreshCw, ExternalLink, UserCircle2, Layers } from 'lucide-react';
import { fetchActiveUsers } from '../api/analyticsApi';

const POLL_INTERVAL_MS = 15 * 1000;

const formatRelative = (iso) => {
    if (!iso) return '—';
    const ts = new Date(iso).getTime();
    if (Number.isNaN(ts)) return '—';
    const diffSec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
    if (diffSec < 5) return 'just now';
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    return `${diffHr}h ago`;
};

const formatTime = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleTimeString('en-AU', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    } catch {
        return '—';
    }
};

const prettyEvent = (name) => {
    if (!name) return '—';
    return name
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

const truncateUrl = (url) => {
    if (!url) return null;
    try {
        const u = new URL(url);
        const path = `${u.pathname}${u.search}`;
        return { host: u.host, path: path === '/' ? '' : path, full: url };
    } catch {
        return { host: url, path: '', full: url };
    }
};

const LiveUsersModal = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState({ active_users: 0, window_minutes: 3, sessions: [] });
    const intervalRef = useRef(null);

    const load = async (isInitial = false) => {
        try {
            if (isInitial) setLoading(true);
            else setRefreshing(true);
            setError(null);
            const resp = await fetchActiveUsers();
            setData({
                active_users: resp?.active_users ?? 0,
                window_minutes: resp?.window_minutes ?? 3,
                sessions: Array.isArray(resp?.sessions) ? resp.sessions : [],
            });
        } catch (err) {
            console.error('Error loading live users:', err);
            setError('Failed to load live users');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return undefined;
        load(true);
        intervalRef.current = setInterval(() => load(false), POLL_INTERVAL_MS);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const sessions = data.sessions || [];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-start justify-between p-5 sm:p-6 border-b border-gray-200">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                            </span>
                            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Live Users</h2>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 mt-1">
                            {data.active_users} {data.active_users === 1 ? 'user' : 'users'} active in the last {data.window_minutes} minutes
                        </p>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <button
                            onClick={() => load(false)}
                            disabled={loading || refreshing}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                            title="Refresh"
                        >
                            <RefreshCw className={`w-5 h-5 text-gray-500 ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Close"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                        </div>
                    ) : error ? (
                        <div className="text-center text-red-600 py-8">{error}</div>
                    ) : sessions.length === 0 ? (
                        <div className="text-center text-gray-500 py-12">
                            <UserCircle2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <p className="font-medium text-gray-700">No live users right now</p>
                            <p className="text-sm text-gray-500 mt-1">Sessions appear here when buyers are active in the portal.</p>
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {sessions.map((s) => {
                                const isAuth = !!s.buyer_id;
                                const displayName = isAuth
                                    ? (s.buyer_name || s.buyer_email || `Buyer #${s.buyer_id}`)
                                    : 'Anonymous visitor';
                                const initials = isAuth
                                    ? (s.buyer_name
                                        ? s.buyer_name.split(' ').map((n) => n[0]).join('').toUpperCase().substring(0, 2)
                                        : (s.buyer_email || 'BU').substring(0, 2).toUpperCase())
                                    : 'AN';
                                const url = truncateUrl(s.page_url);

                                return (
                                    <li
                                        key={s.session_id}
                                        className="border border-gray-200 rounded-xl p-3 sm:p-4 hover:border-gray-300 transition-colors"
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                                                isAuth ? 'bg-blue-100' : 'bg-gray-100'
                                            }`}>
                                                {isAuth ? (
                                                    <span className="text-blue-700 font-bold text-sm">{initials}</span>
                                                ) : (
                                                    <User className="w-5 h-5 text-gray-500" />
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="font-semibold text-gray-900 truncate">
                                                        {displayName}
                                                    </span>
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-700">
                                                        <span className="relative flex h-1.5 w-1.5">
                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                                                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                                                        </span>
                                                        live
                                                    </span>
                                                    {!isAuth && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">
                                                            anonymous
                                                        </span>
                                                    )}
                                                    {s.session_count > 1 && (
                                                        <span
                                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700"
                                                            title="This user has multiple active sessions (tabs/devices) in the live window"
                                                        >
                                                            <Layers className="w-3 h-3" />
                                                            {s.session_count} sessions
                                                        </span>
                                                    )}
                                                </div>

                                                {isAuth && s.buyer_email && (
                                                    <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-1">
                                                        <Mail className="w-3.5 h-3.5 text-gray-400" />
                                                        <span className="truncate">{s.buyer_email}</span>
                                                    </div>
                                                )}

                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mt-2 text-xs text-gray-600">
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        <Activity className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                        <span className="truncate">
                                                            <span className="text-gray-500">Last event:</span>{' '}
                                                            <span className="font-medium text-gray-800">{prettyEvent(s.last_event_name)}</span>
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        <Clock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                        <span className="truncate" title={formatTime(s.last_event_at)}>
                                                            {formatRelative(s.last_event_at)}
                                                        </span>
                                                    </div>
                                                    {url && (
                                                        <div className="flex items-center gap-1.5 min-w-0 sm:col-span-2">
                                                            <Globe className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                            <a
                                                                href={url.full}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="truncate text-gray-700 hover:text-blue-600 inline-flex items-center gap-1"
                                                                title={url.full}
                                                            >
                                                                <span className="truncate">{url.host}{url.path}</span>
                                                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                                                            </a>
                                                        </div>
                                                    )}
                                                    <div className="flex items-center gap-1.5 min-w-0">
                                                        <span className="text-gray-500">Events in window:</span>
                                                        <span className="font-medium text-gray-800">{s.event_count}</span>
                                                    </div>
                                                    {s.utm_source && (
                                                        <div className="flex items-center gap-1.5 min-w-0">
                                                            <span className="text-gray-500">Source:</span>
                                                            <span className="font-medium text-gray-800 truncate">{s.utm_source}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                    <div className="text-xs sm:text-sm text-gray-600">
                        Auto-refreshes every {Math.round(POLL_INTERVAL_MS / 1000)}s
                    </div>
                    <button
                        onClick={onClose}
                        className="px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default LiveUsersModal;
